/**
 * Permission Blocked Dialog
 * Recovery dialog shown when permissions are denied/blocked
 */

import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Collapse,
  Alert,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import BlockIcon from '@mui/icons-material/Block'
import RefreshIcon from '@mui/icons-material/Refresh'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { BrowserSpecificHelp } from './BrowserSpecificHelp'
import { getEncouragementMessage } from 'src/services/mediaPermissions'

export const PermissionBlockedDialog = ({
  open,
  onClose,
  onRetry,
  recoveryPlan,
  attemptCount = 0,
  showSupport = true,
}) => {
  const [showDetailedHelp, setShowDetailedHelp] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)

  if (!recoveryPlan) return null

  const { message, browser, deviceType, isPWA } = recoveryPlan

  const handleRetry = async () => {
    setIsRetrying(true)
    try {
      await onRetry()
    } catch (error) {
      console.error('Error retrying permissions:', error)
    } finally {
      setIsRetrying(false)
    }
  }

  const toggleDetailedHelp = () => {
    setShowDetailedHelp(!showDetailedHelp)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="permission-blocked-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle id="permission-blocked-title">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BlockIcon sx={{ fontSize: 32, color: '#f44336' }} />
            <Typography variant="h6" component="div" fontWeight="bold">
              {message.title}
            </Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Description */}
          <Typography variant="body1" color="text.secondary">
            {message.description}
          </Typography>

          {/* Encouragement message */}
          <Alert severity="info" icon={<HelpOutlineIcon />}>
            {getEncouragementMessage(attemptCount)}
          </Alert>

          {/* Quick help preview */}
          <Box>
            <Button
              fullWidth
              variant="outlined"
              endIcon={showDetailedHelp ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={toggleDetailedHelp}
              sx={{ justifyContent: 'space-between', textAlign: 'left' }}
            >
              <span>
                {showDetailedHelp ? 'Hide' : 'Show'} Step-by-Step Instructions
              </span>
            </Button>
          </Box>

          {/* Detailed help */}
          <Collapse in={showDetailedHelp}>
            <Box sx={{ pt: 2 }}>
              <BrowserSpecificHelp recoveryPlan={recoveryPlan} showVisuals={true} />
            </Box>
          </Collapse>

          {/* Browser/Device info for debugging */}
          {attemptCount > 2 && (
            <Box
              sx={{
                p: 2,
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                fontSize: '0.75rem',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Browser: {browser} | Device: {deviceType} | PWA: {isPWA ? 'Yes' : 'No'}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 2, flexWrap: 'wrap' }}>
        {showSupport && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              // This could open a support chat or navigate to help page
              window.open('/help-center', '_blank')
            }}
            disabled={isRetrying}
          >
            Contact Support
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {browser === 'safari' && (
          <Button
            variant="outlined"
            color="info"
            onClick={() => window.location.reload()}
            disabled={isRetrying}
            sx={{ mr: 1 }}
          >
            Refresh Page
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isRetrying}
        >
          Skip for Now
        </Button>
        <Button
          variant="contained"
          startIcon={isRetrying ? null : <RefreshIcon />}
          onClick={handleRetry}
          disabled={isRetrying}
          sx={{
            backgroundColor: '#514387',
            '&:hover': {
              backgroundColor: '#403266',
            },
            minWidth: 140,
          }}
        >
          {isRetrying ? 'Checking...' : "I've Enabled It"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PermissionBlockedDialog

